'use client';

import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, ArrowUpRight } from '@phosphor-icons/react';
import { Header, Footer } from '@/components/layout';
import { blogApi, type BlogListResponse } from '@/lib/api/blog';
import { JsonLd } from '@/components/seo/JsonLd';
import { generateBreadcrumbSchema } from '@/lib/seo/json-ld';
import { siteConfig } from '@/config/site';
import { BlogCard, BlogGridSkeleton } from '@/components/features/blog/blog-editorial';
import '@/components/features/blog/blog-editorial.css';

export function BlogListClient({ initialData }: { initialData?: BlogListResponse }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const requestedPage = Number(searchParams.get('page'));
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const activeTag = searchParams.get('tag')?.trim() || undefined;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['blog', page, activeTag],
    queryFn: () => blogApi.getList(page, 12, activeTag),
    initialData,
    staleTime: 60000,
  });
  const { data: tags = [] } = useQuery({
    queryKey: ['blog-tags'],
    queryFn: () => blogApi.getTags(),
    staleTime: 300000,
  });
  const posts = data?.data || [];
  const totalPages = data?.total_pages || 1;
  const [featured, ...remaining] = posts;
  const showFeatured = page === 1;
  const gridPosts = showFeatured ? remaining : posts;

  function navigate(nextPage: number, tag?: string) {
    const query = new URLSearchParams();
    if (tag) query.set('tag', tag);
    if (nextPage > 1) query.set('page', String(nextPage));
    router.push(`${pathname}${query.size ? `?${query}` : ''}`, { scroll: false });
    if (nextPage !== page)
      document.getElementById('journal-feed')?.scrollIntoView({ block: 'start' });
  }

  return (
    <div className="blog-editorial journal-page">
      <Header />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Главная', href: siteConfig.url },
          { name: 'Блог', href: `${siteConfig.url}/blog` },
        ])}
      />
      <main className="journal-container journal-main">
        <nav className="journal-breadcrumb" aria-label="Хлебные крошки">
          <Link href="/">Главная</Link>
          <span aria-hidden="true">/</span>
          <span>Блог</span>
        </nav>
        <header className="journal-intro">
          <div>
            <h1>
              Блог Азиаточки<span>.</span>
            </h1>
            <p>Новое в меню, истории и всё, что хочется рассказать.</p>
          </div>
          <Link className="journal-text-link journal-intro-menu" href="/">
            К меню <ArrowUpRight size={19} aria-hidden="true" />
          </Link>
        </header>
        <section id="journal-feed" aria-label="Статьи">
          <div className="journal-toolbar">
            <nav className="journal-filters" aria-label="Темы блога">
              <button type="button" onClick={() => navigate(1)} aria-pressed={!activeTag}>
                Все статьи
              </button>
              {tags.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  onClick={() => navigate(1, tag)}
                  aria-pressed={tag === activeTag}
                >
                  {tag}
                </button>
              ))}
              {activeTag && !tags.includes(activeTag) && (
                <button type="button" aria-pressed="true" onClick={() => navigate(1)}>
                  {activeTag} ×
                </button>
              )}
            </nav>
            {data && (
              <span className="journal-count" aria-live="polite">
                {data.total} материалов
              </span>
            )}
          </div>
          {isLoading ? (
            <BlogGridSkeleton />
          ) : isError ? (
            <div className="journal-empty" role="alert">
              <h2>Не получилось загрузить статьи</h2>
              <p>Попробуйте ещё раз — возможно, соединение прервалось.</p>
              <button type="button" className="journal-button" onClick={() => void refetch()}>
                Попробовать ещё раз
              </button>
            </div>
          ) : posts.length === 0 ? (
            <div className="journal-empty">
              <h2>
                {activeTag ? 'В этой теме пока нет статей' : 'Скоро здесь будет что почитать'}
              </h2>
              <p>
                {activeTag
                  ? 'Посмотрите остальные материалы блога.'
                  : 'Загляните чуть позже — мы готовим новые истории.'}
              </p>
              {activeTag && (
                <button type="button" className="journal-button" onClick={() => navigate(1)}>
                  Все статьи
                </button>
              )}
            </div>
          ) : (
            <>
              {showFeatured && featured && <BlogCard post={featured} featured priority />}
              {gridPosts.length > 0 && (
                <div className="journal-grid">
                  {gridPosts.map((post) => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </>
          )}
          {totalPages > 1 && (
            <nav className="journal-pagination" aria-label="Страницы блога">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => navigate(page - 1, activeTag)}
                aria-label="Предыдущая страница"
              >
                <ArrowLeft size={20} />
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .filter(
                  (value) => value === 1 || value === totalPages || Math.abs(value - page) <= 1
                )
                .map((value, index, visible) => (
                  <span key={value} className="journal-page-group">
                    {index > 0 && value - visible[index - 1] > 1 && (
                      <span className="journal-page-ellipsis" aria-hidden="true">
                        …
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate(value, activeTag)}
                      aria-current={page === value ? 'page' : undefined}
                      aria-label={`Страница ${value}`}
                    >
                      {value}
                    </button>
                  </span>
                ))}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => navigate(page + 1, activeTag)}
                aria-label="Следующая страница"
              >
                <ArrowRight size={20} />
              </button>
            </nav>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function BlogListFallback() {
  return (
    <div className="blog-editorial journal-page">
      <Header />
      <main className="journal-container journal-main">
        <header className="journal-intro">
          <h1>
            Блог Азиаточки<span>.</span>
          </h1>
        </header>
        <BlogGridSkeleton />
      </main>
      <Footer />
    </div>
  );
}
